import { useState, useEffect } from 'react';
import { 
  Shield, 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  TrendingDown, 
  Users,
  FileText,
  BarChart3,
  Sparkles,
  Brain,
  Zap
} from 'lucide-react';
import { useMockUser } from '@/hooks/useMockUser';
import { DashboardData } from '@/types/dashboard';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

// Mock dashboard data
const createMockDashboardData = (): DashboardData => ({
  riskMetrics: {
    totalRisks: 47,
    risksByCategory: {
      operational: 12,
      financial: 8,
      strategic: 6,
      compliance: 9,
      reputational: 4,
      technology: 5,
      cyber_security: 3
    },
    risksByPriority: {
      low: 18,
      medium: 20,
      high: 7,
      critical: 2
    },
    averageRiskScore: 6.2,
    trendsOverTime: [
      { period: '2024-Q1', totalRisks: 42, averageScore: 5.8, mitigatedRisks: 8 },
      { period: '2024-Q2', totalRisks: 45, averageScore: 6.1, mitigatedRisks: 12 },
      { period: '2024-Q3', totalRisks: 47, averageScore: 6.2, mitigatedRisks: 15 },
    ]
  },
  complianceOverview: {
    totalFrameworks: 6,
    frameworkStatus: {
      'DORA': 'partially-compliant',
      'NIST': 'compliant',
      'ISO 27001': 'compliant',
      'SOX': 'partially-compliant',
      'BASEL III': 'non-compliant',
      'GDPR': 'compliant'
    },
    overallComplianceScore: 78,
    criticalGaps: 3,
    upcomingAssessments: 2
  },
  recentActivities: [
    {
      id: '1',
      type: 'risk_updated',
      title: 'Updated Cyber Security Risk Assessment',
      user: 'Sarah Chen',
      timestamp: new Date('2024-11-05T08:45:00Z'),
      resource: 'Risk Register',
      priority: 'high'
    },
    {
      id: '2',
      type: 'control_tested',
      title: 'Multi-Factor Authentication Control Testing',
      user: 'Mark Thompson',
      timestamp: new Date('2024-11-05T07:30:00Z'),
      resource: 'Controls',
      priority: 'medium'
    },
    {
      id: '3',
      type: 'compliance_assessed',
      title: 'DORA Framework Compliance Review',
      user: 'Elena Rodriguez',
      timestamp: new Date('2024-11-04T16:15:00Z'),
      resource: 'Compliance',
      priority: 'high'
    }
  ],
  workflowStatus: {
    total: 23,
    pending: 7,
    inProgress: 9,
    overdue: 3,
    completedThisWeek: 4
  },
  keyPerformanceIndicators: [
    {
      id: '1',
      name: 'Risk Mitigation Rate',
      value: 85,
      unit: '%',
      target: 90,
      trend: 'up',
      trendPercentage: 5.2,
      category: 'risk'
    },
    {
      id: '2',
      name: 'Compliance Score',
      value: 78,
      unit: '%',
      target: 85,
      trend: 'up',
      trendPercentage: 3.1,
      category: 'compliance'
    },
    {
      id: '3',
      name: 'Open High-Risk Items',
      value: 7,
      unit: 'items',
      target: 5,
      trend: 'down',
      trendPercentage: -12.5,
      category: 'risk'
    },
    {
      id: '4',
      name: 'Control Effectiveness',
      value: 92,
      unit: '%',
      target: 95,
      trend: 'stable',
      trendPercentage: 0.8,
      category: 'operational'
    }
  ]
});

export function DashboardPage() {
  const { userProfile } = useMockUser();
  const { sectionColor } = useNavigation();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate API call
    const timer = setTimeout(() => {
      setDashboardData(createMockDashboardData());
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-400">Unable to load dashboard data</p>
      </div>
    );
  }

  const { user } = userProfile;

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Welcome Header with Themis Context */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={cn("text-3xl font-bold", sectionColor)}>
              Welcome back, {user.firstName}
            </h1>
            <p className="text-neutral-400 mt-2">
              Themis-powered risk and compliance overview for {user.organization}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-neutral-400">Last updated</p>
            <p className="text-white font-medium">
              {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>
      </div>

      {/* Themis Executive Summary */}
      <div className="card border-2 border-primary-500/30 bg-gradient-to-br from-primary-500/5 to-cyan-500/5">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-gradient-to-r from-primary-500 to-cyan-500">
            <Brain className="w-6 h-6 text-black" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-lg font-semibold text-white">Themis Executive Summary</h3>
              <span className="badge badge-primary text-xs">Generated 2 mins ago</span>
            </div>
            <div className="space-y-3 text-neutral-300">
              <p className="leading-relaxed">
                <span className="font-semibold text-primary-400">⚠️ Attention Required:</span> Your organization has <span className="font-semibold text-danger-400">2 critical</span> and <span className="font-semibold text-warning-400">7 high-priority</span> risks requiring immediate action. 
                The AI analysis identifies <span className="font-semibold text-white">cyber security</span> as the highest impact area, with emerging threats detected in your cloud infrastructure.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-success-400">✓ Positive Trends:</span> Risk mitigation rate has improved by <span className="font-semibold text-success-400">5.2%</span> this quarter. 
                Control effectiveness is at <span className="font-semibold text-white">92%</span>, approaching your 95% target. 
                NIST, ISO 27001, and GDPR frameworks are fully compliant.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-cyan-400">🎯 Themis Recommendations:</span> Focus resources on DORA and Basel III compliance gaps (3 critical findings). 
                Consider increasing staffing for operational risk assessments—current utilization at 81% with demand projected to increase 15-25% next quarter.
                Schedule supplier due diligence reviews for 12 high-risk vendors flagged by the Themis risk scoring model.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Themis-Generated Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Critical Alerts */}
        <div className="card border-l-4 border-danger-500">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-danger-500/20">
              <Zap className="w-5 h-5 text-danger-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-white mb-2">Themis Alert: Critical Risks</h4>
              <p className="text-xs text-neutral-400 mb-3">
                2 risks escalated to critical in the last 48 hours. Cloud infrastructure vulnerability detected with high exploitation likelihood.
              </p>
              <button className="text-xs text-danger-400 hover:text-danger-300 font-medium">
                View Details →
              </button>
            </div>
          </div>
        </div>

        {/* Predictive Analysis */}
        <div className="card border-l-4 border-cyan-500">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <Sparkles className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-white mb-2">Predictive Insights</h4>
              <p className="text-xs text-neutral-400 mb-3">
                AI forecasts 23% increase in regulatory requirements next quarter. Proactive assessment recommended for 5 key processes.
              </p>
              <button className="text-xs text-cyan-400 hover:text-cyan-300 font-medium">
                View Forecast →
              </button>
            </div>
          </div>
        </div>

        {/* Optimization Opportunities */}
        <div className="card border-l-4 border-success-500">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-success-500/20">
              <Brain className="w-5 h-5 text-success-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-white mb-2">Optimization Found</h4>
              <p className="text-xs text-neutral-400 mb-3">
                AI identified 14 redundant controls across 3 frameworks. Consolidation could save 120 hours/month in manual testing.
              </p>
              <button className="text-xs text-success-400 hover:text-success-300 font-medium">
                Review Suggestions →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardData.keyPerformanceIndicators.map((kpi) => (
          <div key={kpi.id} className="card hover:border-primary-500/50 transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-sm text-neutral-400">{kpi.name}</p>
                  <div className="group relative">
                    <Sparkles className="w-3 h-3 text-primary-400" />
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs text-white bg-neutral-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      Themis-tracked metric
                    </span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-white">
                  {kpi.value}{kpi.unit}
                </p>
                {kpi.target && (
                  <p className="text-xs text-neutral-500 mt-1">
                    Target: {kpi.target}{kpi.unit}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                {kpi.trend === 'up' && <TrendingUp className="text-success-500" size={16} />}
                {kpi.trend === 'down' && <TrendingDown className="text-danger-500" size={16} />}
                {kpi.trend === 'stable' && <BarChart3 className="text-neutral-400" size={16} />}
                <span 
                  className={`text-xs font-medium ${
                    kpi.trend === 'up' ? 'text-success-500' : 
                    kpi.trend === 'down' ? 'text-danger-500' : 
                    'text-neutral-400'
                  }`}
                >
                  {kpi.trendPercentage > 0 ? '+' : ''}{kpi.trendPercentage}%
                </span>
              </div>
            </div>
            <div className="text-xs text-neutral-500 border-t border-neutral-800 pt-2">
              Themis prediction: {kpi.trend === 'up' ? 'Continued improvement' : kpi.trend === 'down' ? 'Declining, action needed' : 'Stable performance'}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Risk Overview */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center space-x-2">
                <Shield size={20} />
                <span>Risk Overview</span>
              </div>
            </div>
            <div className="space-y-6">
              {/* Risk Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">
                    {dashboardData.riskMetrics.totalRisks}
                  </p>
                  <p className="text-sm text-neutral-400">Total Risks</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-danger-400">
                    {dashboardData.riskMetrics.risksByPriority.critical}
                  </p>
                  <p className="text-sm text-neutral-400">Critical</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-warning-400">
                    {dashboardData.riskMetrics.risksByPriority.high}
                  </p>
                  <p className="text-sm text-neutral-400">High</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary-400">
                    {dashboardData.riskMetrics.averageRiskScore}
                  </p>
                  <p className="text-sm text-neutral-400">Avg Score</p>
                </div>
              </div>

              {/* Risk by Category */}
              <div>
                <h4 className="text-sm font-medium text-white mb-3">Risk Distribution by Category</h4>
                <div className="space-y-2">
                  {Object.entries(dashboardData.riskMetrics.risksByCategory).map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between">
                      <span className="text-sm text-neutral-400 capitalize">
                        {category.replace('_', ' ')}
                      </span>
                      <div className="flex items-center space-x-2">
                        <div className="w-24 bg-neutral-800 rounded-full h-2">
                          <div 
                            className="bg-primary-500 h-2 rounded-full" 
                            style={{ width: `${(count / dashboardData.riskMetrics.totalRisks) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-white font-medium w-8">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Themis Risk Insights */}
              <div className="mt-6 p-4 bg-gradient-to-r from-primary-500/10 to-cyan-500/10 rounded-lg border border-primary-500/30">
                <div className="flex items-start gap-2">
                  <Brain className="w-4 h-4 text-primary-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h5 className="text-xs font-semibold text-primary-400 mb-2">Themis Risk Analysis</h5>
                    <p className="text-xs text-neutral-300 leading-relaxed">
                      Cyber security risks show clustering patterns suggesting systemic vulnerability. 
                      Financial risks correlate with market volatility (+18% last week). 
                      Themis recommends immediate review of 3 operational risks due to changing business processes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Status */}
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center space-x-2">
                <Activity size={20} />
                <span>Compliance Status</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-3xl font-bold gradient-text">
                  {dashboardData.complianceOverview.overallComplianceScore}%
                </p>
                <p className="text-sm text-neutral-400">Overall Compliance</p>
              </div>

              <div className="space-y-3">
                {Object.entries(dashboardData.complianceOverview.frameworkStatus).map(([framework, status]) => (
                  <div key={framework} className="flex items-center justify-between">
                    <span className="text-sm text-white">{framework}</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      status === 'compliant' ? 'badge-success' :
                      status === 'partially-compliant' ? 'badge-warning' :
                      status === 'non-compliant' ? 'badge-danger' :
                      'badge-secondary'
                    }`}>
                      {status === 'compliant' && <CheckCircle size={12} className="mr-1" />}
                      {status === 'non-compliant' && <AlertTriangle size={12} className="mr-1" />}
                      {status === 'partially-compliant' && <Clock size={12} className="mr-1" />}
                      {status.replace('-', ' ')}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-neutral-700 pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-400">Critical Gaps</span>
                  <span className="text-sm text-danger-400 font-medium">
                    {dashboardData.complianceOverview.criticalGaps}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-400">Upcoming Assessments</span>
                  <span className="text-sm text-warning-400 font-medium">
                    {dashboardData.complianceOverview.upcomingAssessments}
                  </span>
                </div>
              </div>

              {/* Themis Compliance Insights */}
              <div className="mt-4 p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-lg border border-purple-500/30">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h5 className="text-xs font-semibold text-purple-400 mb-2">Themis Compliance Recommendation</h5>
                    <p className="text-xs text-neutral-300 leading-relaxed">
                      Basel III non-compliance requires 4-6 week remediation. DORA gaps are addressable within 2 weeks. 
                      Themis suggests prioritizing DORA (regulatory deadline Q1 2025) before Basel III.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Workflow Status */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center space-x-2">
                <Users size={20} />
                <span>Workflow Status</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-xl font-bold text-white">
                    {dashboardData.workflowStatus.total}
                  </p>
                  <p className="text-xs text-neutral-400">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-primary-400">
                    {dashboardData.workflowStatus.inProgress}
                  </p>
                  <p className="text-xs text-neutral-400">In Progress</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-400">Pending</span>
                  <span className="text-sm text-warning-400">
                    {dashboardData.workflowStatus.pending}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-400">Overdue</span>
                  <span className="text-sm text-danger-400">
                    {dashboardData.workflowStatus.overdue}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-400">Completed This Week</span>
                  <span className="text-sm text-success-400">
                    {dashboardData.workflowStatus.completedThisWeek}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center space-x-2">
            <FileText size={20} />
            <span>Recent Activities</span>
          </div>
        </div>
        <div className="space-y-4">
          {dashboardData.recentActivities.map((activity) => (
            <div key={activity.id} className="flex items-start space-x-4 p-4 border border-neutral-800 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center">
                  <Activity size={16} className="text-primary-400" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-white">
                    {activity.title}
                  </p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    activity.priority === 'high' ? 'badge-danger' :
                    activity.priority === 'medium' ? 'badge-warning' :
                    'badge-success'
                  }`}>
                    {activity.priority}
                  </span>
                </div>
                <p className="text-sm text-neutral-400">
                  by {activity.user} in {activity.resource}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  {activity.timestamp.toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}